import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { openUrl } from 'frontend-utils/utils/url/open-url';

// Right value: internal name to use in the formData when sending attachments
const FILE_FIELD_MAPPING = {
  extranet_screenshot: 'parity_manager.issue.upload.extranet_file',
  additional_screenshot: 'parity_manager.issue.upload.additional_file',
};

// Max allowed size of attachments of 10MB (= 10485760 bytes)
const MAX_ISSUE_ATTACHMENT_FILE_SIZE = 10485760;

export default class MarketParityParityManagerIssueuploadController extends Controller {
  queryParams = [
    {
      issueId: { scope: 'controller' },
      subscriptionId: { scope: 'controller' },
      issueType: { scope: 'controller' },
    },
  ];

  @service ajax;
  @service alerting;
  @service errorTracking;
  @service('hotel-and-market') hotelAndMarketService;
  @service intl;
  @service router;

  @tracked issueId;
  @tracked issueType;
  @tracked isUploadingFile = false;
  @tracked subscriptionId;

  get isUploadDisabled() {
    return (
      (this.model?.extranetScreenshot && this.model?.additionalScreenshot) ||
      this.isUploadingFile
    );
  }

  get market() {
    return this.hotelAndMarketService.market;
  }

  @action
  checkFileSize(event) {
    // This is fired in the onchange of the file inputs in the form of the issue upload modal
    // Main purpose is to show feedback to the user if they select files that exceed the limit
    if (event.srcElement.files && event.srcElement.files[0]) {
      if (event.srcElement.files[0].size > MAX_ISSUE_ATTACHMENT_FILE_SIZE) {
        this.alerting.showWarningAlert({
          text: this.intl.t(
            'parity_manager.issue.upload.error.file_size_exceeded',
          ),
        });

        // Clear the value of the file input
        event.srcElement.value = '';
      }
    }
  }

  @action
  deleteIssueFile(fileField) {
    if (fileField) {
      this.ajax
        .request(
          `${this.ajax.host}/apigateway/v1/app/paritymanager/delete_attachments?market_id=${this.market?.id}&subscription_id=${this.subscriptionId}&issue_id=${this.issueId}&file_field=${fileField}`,
          {
            method: 'DELETE',
          },
        )
        .then((/* response */) => {
          // This will request the attachments attached to the issue again. We should then see the updated attachments then.
          this.send('refreshRoute');
        })
        .catch((error) => {
          this.errorTracking.captureException(error);
        });
    }
  }

  @action
  downloadIssueFile(fileUrl) {
    openUrl(fileUrl);
  }

  @action
  redirectOnClose() {
    this.router.legacyTransitionTo('market.parity.parity-manager');
  }

  @action
  submitIssueUploadForm() {
    const formData = new FormData();

    const formFileInputs = document
      .getElementById('issue-upload-form')
      .querySelectorAll('input.issue-upload-form-file');

    let areAllSelectedFilesWithinMaxFileSize = true;

    if (formFileInputs.length) {
      formFileInputs.forEach((fileInput) => {
        if (fileInput.files[0]) {
          // Do a basic file size check. We don't want people crashing our backend by selecting a file of 1GB
          if (fileInput.files[0].size < MAX_ISSUE_ATTACHMENT_FILE_SIZE) {
            formData.append(
              fileInput.getAttribute('data-name'),
              fileInput.files[0],
            );
          } else {
            areAllSelectedFilesWithinMaxFileSize = false;
          }
        }
      });
    }

    if (areAllSelectedFilesWithinMaxFileSize) {
      this.isUploadingFile = true;

      this.ajax
        .request(
          `${this.ajax.host}/apigateway/v1/app/paritymanager/save_attachments?market_id=${this.market?.id}&subscription_id=${this.subscriptionId}&issue_id=${this.issueId}&issue_type=${this.issueType}`,
          {
            method: 'POST',
            contentType: false,
            contentLength: formData.length,
            processData: false,
            mimeType: 'multipart/form-data',
            data: formData,
          },
        )
        .then((/* response */) => {
          this.isUploadingFile = false;

          // This will request the attachments attached to the issue again. We should then see the updated attachments then.
          this.send('refreshRoute');
        })
        .catch((error) => {
          if (error) {
            this.errorTracking.captureException(error);
          }

          this.isUploadingFile = false;

          const content = document.createElement('div');

          // Check if the API returned a specific error message
          if (error?.payload.errors && error.payload.errors.length) {
            const errorMessages = error.payload.errors.reduce(
              (accumulator, { meta }) => {
                const messages = Object.keys(meta).map((key) => {
                  if (
                    !this.intl.exists(FILE_FIELD_MAPPING[key]) ||
                    !this.intl.exists(meta[key][0])
                  ) {
                    return undefined;
                  }

                  return [
                    '<p style="margin-bottom:8px;" class="paragraph-300--regular">',
                    `  <span style="font-weight:bold;">${this.intl.t(FILE_FIELD_MAPPING[key])}</span>: ${this.intl.t(meta[key][0])}`,
                    '</p>',
                  ].join('\n');
                });

                return [...accumulator, ...messages];
              },
              [],
            );

            const translationMissing = errorMessages.some(
              (message) => message === undefined,
            );

            content.innerHTML = translationMissing
              ? this.intl.t('general.error')
              : errorMessages.join('/n');
          } else {
            content.innerHTML = this.intl.t('general.error');
          }

          this.alerting.showErrorAlert({
            content,
          });
        });
    }
  }
}
